import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { Title, Text, Checkbox } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/sharp-regular-svg-icons";
import { format } from "date-fns";
import { parseDateOnly } from "shared/src/utils/date-utils";
import { gql } from "@/__generated__";
import Loader from "@/components/shared/loader";
import { StatusIndicator } from "@/components/status-indicator/status-indicator";
import { Button } from "@/components/buttons/button";
import { AppealLetterViewerModal } from "@/components/appeal-letter-viewer-modal";
import { Toast } from "design-system/src/components/toast";
import styles from "./strengthen-referral-header.module.scss";

export const GET_PATIENT_INFO = gql(`
  query GetPatientInfoForStrengthen($appealId: ID!) {
    getAppealById(appealId: $appealId) {
      appealId
      referral {
        patientEmail
      }
      isExpeditedReview
      patient {
        firstName
        lastName
        dateOfBirth
      }
      denial {
        condition
        medicationBrandName
      }
      status {
        referred
        insuranceCardUpload
        insuranceCardUploadSkipped
        paymentSuccessful
        issueOutstanding
        denialUpheld
        denialOverturned
        appealSubmitted
        pdfGenerated
        closed
        currentStatus
      }
    }
  }
`);

const SAVE_IS_EXPEDITED_REVIEW = gql(`
  mutation SaveIsExpeditedReviewFromConsole($appealId: ID!, $isExpeditedReview: Boolean!) {
    saveIsExpeditedReview(appealId: $appealId, isExpeditedReview: $isExpeditedReview)
  }
`);

interface Props {
  appealId: string;
}

export function StrengthenReferralHeader({ appealId }: Props) {
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isExpeditedLocal, setIsExpeditedLocal] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery(GET_PATIENT_INFO, {
    variables: { appealId },
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (data?.getAppealById) {
      setIsExpeditedLocal(Boolean(data.getAppealById.isExpeditedReview));
    }
  }, [data?.getAppealById?.isExpeditedReview]);

  const [saveIsExpeditedReview, { loading: saveLoading }] = useMutation(
    SAVE_IS_EXPEDITED_REVIEW,
  );

  const handleExpeditedChange = async (nextChecked: boolean) => {
    const previous = isExpeditedLocal;
    setIsExpeditedLocal(nextChecked);
    setSaveError(null);
    try {
      await saveIsExpeditedReview({
        variables: { appealId, isExpeditedReview: nextChecked },
      });
      await refetch();
    } catch (mutationError) {
      setIsExpeditedLocal(previous);
      setSaveError(
        mutationError instanceof Error
          ? mutationError.message
          : "Failed to update expedited review",
      );
    }
  };

  if (loading && !data) {
    return (
      <div className={styles.loadingContainer}>
        <Loader color="loader_almost_black" width="36" height="36" />
      </div>
    );
  }

  if (error || !data?.getAppealById) {
    return (
      <div className={styles.header}>
        <Title order={2}>Strengthen Referral</Title>
        <Text c="red">Unable to load patient information</Text>
      </div>
    );
  }

  const appeal = data.getAppealById;
  const patient = appeal.patient;
  const denial = appeal.denial;

  const patientName = patient
    ? `${patient.firstName} ${patient.lastName}`
    : "Unknown";
  const dateOfBirth = patient?.dateOfBirth
    ? format(parseDateOnly(patient.dateOfBirth), "MM/dd/yyyy")
    : "N/A";
  const email = appeal.referral?.patientEmail || "N/A";

  const canViewAppealLetter = Boolean(appeal.status.pdfGenerated);
  const hasBeenReferred = Boolean(appeal.status.referred);
  const expeditedDisabled = hasBeenReferred || saveLoading;

  return (
    <>
      <Toast variant="error" isVisible={!!saveError}>
        {saveError}
      </Toast>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <div>
            <Title order={2} className={styles.title}>
              {patientName}
            </Title>
            <Text className={styles.appealId}>ID: {appeal.appealId}</Text>
          </div>
          <StatusIndicator currentStatus={appeal.status.currentStatus ?? ""} />
        </div>

        <div className={styles.patientInfoContainer}>
          <div className={styles.patientInfoItem}>
            <Text className={styles.label}>Patient name</Text>
            <Text className={styles.value}>{patientName}</Text>
          </div>
          <div className={styles.patientInfoItem}>
            <Text className={styles.label}>DoB</Text>
            <Text className={styles.value}>{dateOfBirth}</Text>
          </div>
          <div className={styles.patientInfoItem}>
            <Text className={styles.label}>Email</Text>
            <Text className={styles.value}>{email}</Text>
          </div>
          <div className={styles.patientInfoItem}>
            <Text className={styles.label}>Condition</Text>
            <Text className={styles.value}>{denial?.condition || "N/A"}</Text>
          </div>
          <div className={styles.patientInfoItem}>
            <Text className={styles.label}>Medication</Text>
            <Text className={styles.value}>
              {denial?.medicationBrandName || "N/A"}
            </Text>
          </div>
          <div className={styles.patientInfoItem}>
            <Checkbox
              label="Request expedited review"
              description="For urgent cases. Insurers must respond within 72 hours by law."
              data-testid="expedited-review-input"
              checked={isExpeditedLocal}
              disabled={expeditedDisabled}
              onChange={(event) =>
                handleExpeditedChange(event.currentTarget.checked)
              }
            />
          </div>
          {canViewAppealLetter && (
            <div className={styles.viewAppealLetterItem}>
              <Button
                variant="secondary"
                size="2xs"
                onClick={() => setIsViewerOpen(true)}
              >
                <FontAwesomeIcon icon={faEye} />
                View Appeal Letter
              </Button>
            </div>
          )}
        </div>
      </div>

      <AppealLetterViewerModal
        opened={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        appealId={appealId}
        patientName={patientName}
      />
    </>
  );
}
